// Order-preserving bounded map.
//
// `Promise.all(items.map(worker))` starts every task in the same tick. That is fine for a
// handful of items and a real problem for a bulk file selection: a 100-300 file upload opened
// that many concurrent `generate_upload_url` requests and GCS PUTs at once, and a 100-300 file
// validation decoded every image simultaneously. Both scale with what the user selected, not
// with what the browser or the API can actually serve.
//
// `results[i]` always corresponds to `items[i]` however the workers interleave — callers pair
// results with file names by array position, so the order is part of the contract.
export const mapWithConcurrency = async <T, R>(items: T[], limit: number, worker: (item: T, index: number) => Promise<R>): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;

  const runner = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await worker(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, runner));

  return results;
};

// How many files may be uploaded at once. Every upload is TWO round-trips (a
// `generate_upload_url` POST to our API, then a PUT straight to GCS). 6 is the usual browser
// per-host ceiling for HTTP/1.1 and keeps a large batch progressing steadily instead of
// stalling — our API's `generate_upload_url` is a sync handler sharing a per-worker threadpool
// of 15 with every other sync endpoint, so a single user must not be able to saturate it.
export const UPLOAD_CONCURRENCY = 6;

// How many images may be decoded at once during client-side validation. Higher than the upload
// bound because it is CPU/memory-local with no network leg, but still bounded: each in-flight
// decode holds a decoded bitmap.
export const VALIDATION_CONCURRENCY = 8;
